import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, UserPlus, Trash2, MailCheck, Clock } from 'lucide-react';

import { Card } from './AdminPanelPrimitives';
import { ADMIN_PALETTE, EmptyState } from './adminDesignSystem';
import { formatEuropeanDateTime } from '../../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InviteMainAdminModal = ({ onClose, onInvite, busy }) => {
  const [email, setEmail] = useState('');
  const submit = (event) => {
    event.preventDefault();
    if (!email.includes('@')) return;
    onInvite(email.trim().toLowerCase());
  };
  return (
    <div
      data-testid="admin-main-admin-invite-modal"
      role="dialog"
      onClick={() => !busy && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{ maxWidth: 460, width: '100%', background: '#FFF', borderRadius: 22, padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'grid', gap: 14 }}
      >
        <div>
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 19, margin: 0 }}>Invite new main admin</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
            One other main admin will receive a one-click confirmation email. Once they confirm, the user is promoted to main admin.
          </p>
        </div>
        <input
          data-testid="admin-main-admin-invite-email"
          type="email"
          placeholder="founder@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ padding: '12px 14px', borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 14 }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" data-testid="admin-main-admin-invite-cancel" onClick={onClose} disabled={busy} style={{ padding: '8px 14px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFF', color: '#333', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button type="submit" data-testid="admin-main-admin-invite-confirm" disabled={busy} style={{ padding: '8px 16px', borderRadius: 9999, border: 'none', background: ADMIN_PALETTE.brandRed, color: '#FFF', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminMainAdminsPanel = ({ headers, currentUser }) => {
  const [mainAdmins, setMainAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const seatsRemaining = 3 - (mainAdmins?.length || 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/main-admins/list`, { headers });
      setMainAdmins(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load main admins');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (admin) => {
    if (!window.confirm(`Request removal of ${admin.email}? One other main admin must click the email link to finalise.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/main-admins/${admin.id}/delete-request`, {}, { headers });
      toast.success(`Deletion request sent — ${res.data?.emails_sent || 0} peer email${res.data?.emails_sent === 1 ? '' : 's'} dispatched`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send deletion request');
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (email) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/main-admins/invite`, { email }, { headers });
      toast.success(`Invite sent — ${res.data?.emails_sent || 0} peer email${res.data?.emails_sent === 1 ? '' : 's'} dispatched`);
      setInviteOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-main-admins-panel" style={{ display: 'grid', gap: 12 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Crown size={22} color="#E5A800" />
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, margin: 0 }}>Main admins</h2>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B7280' }}>
              The first 3 accounts to register after a launch reset. They have full power and confirm critical actions for each other via email.
            </p>
          </div>
          {seatsRemaining > 0 && mainAdmins.length >= 2 ? (
            <button
              type="button"
              data-testid="admin-main-admin-invite-button"
              onClick={() => setInviteOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 14px',
                borderRadius: 12,
                border: 'none',
                background: ADMIN_PALETTE.brandRed,
                color: '#FFF',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <UserPlus size={14} />
              Invite main admin
            </button>
          ) : null}
        </div>
      </Card>

      {loading ? (
        <Card><p style={{ textAlign: 'center', color: '#999' }}>Loading…</p></Card>
      ) : mainAdmins.length === 0 ? (
        <EmptyState
          icon={Crown}
          tone="hold"
          title="No main admins yet"
          body="Once 3 users register, they automatically become main admins."
          dataTestId="admin-main-admins-empty"
        />
      ) : (
        <Card style={{ display: 'grid', gap: 10 }}>
          {mainAdmins.map((admin) => {
            const isSelf = currentUser?.id === admin.id;
            return (
              <div
                key={admin.id}
                data-testid={`admin-main-admin-row-${admin.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, border: '1px solid #E5E7EB', background: '#FFFFFF' }}
              >
                <span style={{ width: 42, height: 42, borderRadius: '50%', background: '#FFF8DD', color: '#7A5A00', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                  <Crown size={18} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F1115' }}>
                    {admin.name || admin.email} {isSelf ? <span style={{ marginLeft: 6, fontSize: 11, color: '#7A5A00', fontWeight: 700 }}>(you)</span> : null}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>{admin.email}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                    Joined {admin.created_at ? formatEuropeanDateTime(admin.created_at) : '—'}
                  </p>
                </div>
                {!isSelf ? (
                  <button
                    type="button"
                    data-testid={`admin-main-admin-delete-${admin.id}`}
                    onClick={() => handleDelete(admin)}
                    disabled={busy}
                    title="Request removal — needs one peer main admin to confirm via email"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, border: '1px solid #FBD3D3', background: '#FFF1F1', color: '#B91C1C', fontWeight: 700, fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer' }}
                  >
                    <Trash2 size={13} /> Request removal
                  </button>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#7A5A00', padding: '4px 8px', borderRadius: 9999, background: '#FFF8DD' }}>Locked for self</span>
                )}
              </div>
            );
          })}
        </Card>
      )}

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3B404B' }}>
          <MailCheck size={16} />
          <p style={{ margin: 0, fontSize: 12 }}>
            All critical main-admin actions (add seat, remove seat, app reset) require a one-click confirmation email
            to a peer main admin. Initiator counts as one confirmation automatically.
          </p>
        </div>
      </Card>

      {inviteOpen ? (
        <InviteMainAdminModal onClose={() => setInviteOpen(false)} onInvite={handleInvite} busy={busy} />
      ) : null}
    </div>
  );
};

export const ResetLaunchModal = ({ headers, currentUser, onClose }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const isMainAdmin = Boolean(currentUser?.is_main_admin);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/launch-reset/status`, { headers });
      setStatus(res.data);
    } catch (err) {
      // Quiet — modal still renders.
    }
  }, [headers]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleRequest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/launch-reset/request`, {}, { headers });
      toast.success(`Reset emails dispatched · ${res.data?.emails_sent || 0} sent`);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to request reset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-launch-reset-modal"
      role="dialog"
      onClick={() => !busy && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10600, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520, width: '100%', background: '#FFF', borderRadius: 22, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'grid', gap: 14 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: '#FFE2E2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={20} color={ADMIN_PALETTE.brandRed} />
          </span>
          <div>
            <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 21, margin: 0, color: ADMIN_PALETTE.brandRed }}>Reset Allure</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
              Wipes every account, transaction, message and venue. The first 3 users to register after the reset become the new main admins.
            </p>
          </div>
        </div>

        {!isMainAdmin ? (
          <p style={{ color: '#B91C1C', fontWeight: 700 }}>Only main admins can run a reset.</p>
        ) : status?.pending ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <p style={{ margin: 0, fontWeight: 700, color: '#0F1115' }}>
              Reset already requested — waiting on confirmations
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>
              Initiated by <strong>{status.initiator_email}</strong>.<br />
              All main admins must click the link in their email before the reset executes.
            </p>
            <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
              {(status.required || []).map((row) => (
                <div key={row.user_id} data-testid={`admin-launch-reset-confirm-row-${row.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: row.confirmed ? '#EAF7EE' : '#FFF8DD', border: `1px solid ${row.confirmed ? '#BCE6C8' : '#F4E4A8'}` }}>
                  {row.confirmed ? <MailCheck size={14} color="#0F6B19" /> : <Clock size={14} color="#A36500" />}
                  <span style={{ flex: 1, fontSize: 13, color: '#0F1115' }}>{row.email}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: row.confirmed ? '#0F6B19' : '#A36500' }}>
                    {row.confirmed ? 'Confirmed' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6B7280' }}>
              Link expires {status.expires_at ? formatEuropeanDateTime(status.expires_at) : 'in 24h'}.
            </p>
          </div>
        ) : (
          <>
            <div style={{ padding: '12px 14px', borderRadius: 14, background: '#FFF8DD', border: '1px solid #F4E4A8', fontSize: 12, color: '#7A5A00', lineHeight: 1.5 }}>
              <strong>Heads up.</strong> Tapping "Send reset emails" will email every main admin a one-click "Confirm reset"
              link. The wipe only runs once <strong>all main admins have confirmed</strong>. Audit logs and pending-action
              history are preserved.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#0F1115' }}>
              <input
                type="checkbox"
                data-testid="admin-launch-reset-ack"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              I understand this will permanently delete every account, including mine.
            </label>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="admin-launch-reset-cancel"
            onClick={onClose}
            disabled={busy}
            style={{ padding: '8px 14px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFF', color: '#333', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            Close
          </button>
          {isMainAdmin && !status?.pending ? (
            <button
              type="button"
              data-testid="admin-launch-reset-send-emails"
              onClick={handleRequest}
              disabled={busy || !acknowledged}
              style={{
                padding: '8px 16px',
                borderRadius: 9999,
                border: 'none',
                background: ADMIN_PALETTE.brandRed,
                color: '#FFF',
                fontWeight: 700,
                fontSize: 13,
                cursor: busy || !acknowledged ? 'not-allowed' : 'pointer',
                opacity: busy || !acknowledged ? 0.7 : 1,
              }}
            >
              {busy ? 'Sending…' : 'Send reset emails'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminMainAdminsPanel;
